<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { DateTime } from 'luxon'
import { useDashboardToast } from '@/composables/useDashboardToast'
import { useManageAuthorisationModal } from '@/composables/useManageAuthorisationModal'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'

const { t } = useI18n()
const store = useStore()
const { showToast } = useDashboardToast()
const { isOpen, closeManageAuthorisationModal } = useManageAuthorisationModal()

const isSubmitting = ref(false)

watch(isOpen, (open) => {
  if (!open) {
    isSubmitting.value = false
  }
})

const sendDate = (value: string) => DateTime.fromISO(value).toFormat('yyyy-MM-dd')

const deactivateAuthorisation = async () => {
  isSubmitting.value = true
  try {
    await store.dispatch('deleteAuth', {
      endDate: sendDate(new Date().toISOString()),
    })
    store.commit('CLEAR_SWIFTAID_AUTHORISATION')
    await store.dispatch('fetchAuth')
    closeManageAuthorisationModal()
    showToast(t('Dashboard.Status.ManageModal.DeactivatedToast'))
  } catch (error) {
    console.error('Failed to deactivate Swiftaid authorisation', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <DashboardModal
    :is-open="isOpen"
    modal-class="sa-manage-auth-modal"
    aria-labelledby="deactivate-auth-title"
    aria-describedby="deactivate-auth-desc"
    :close-disabled="isSubmitting"
    @close="closeManageAuthorisationModal"
  >
      <div
        class="sa-modal-icon sa-manage-auth-modal__icon sa-manage-auth-modal__icon--warning"
        aria-hidden="true"
      >
        <DashboardNavIcon name="matching-inactive" />
      </div>

      <h2 id="deactivate-auth-title" class="sa-manage-auth-modal__title">
        {{ $t('Dashboard.Status.ManageModal.Title') }}
      </h2>

      <p id="deactivate-auth-desc" class="sa-manage-auth-modal__intro">
        {{ $t('Dashboard.Status.ManageModal.Intro') }}
      </p>

    <template #footer>
      <div class="sa-modal-actions sa-manage-auth-modal__actions">
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="closeManageAuthorisationModal"
        >
          {{ $t('Buttons.Cancel') }}
        </button>
        <button
          type="button"
          class="sa-btn-secondary sa-btn-unauthorise"
          :disabled="isSubmitting"
          :aria-busy="isSubmitting"
          @click="deactivateAuthorisation"
        >
          <span v-if="isSubmitting" class="spinner sa-btn-spinner" aria-hidden="true" />
          <span>{{ $t('Dashboard.Status.ManageModal.Confirm') }}</span>
        </button>
      </div>
    </template>
  </DashboardModal>
</template>
